import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { WarningOutlined } from '@ant-design/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';

import QueryBoundary from 'components/QueryBoundary/QueryBoundary';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import PrecoOfferDetails from './PrecoOfferDetails';
import PrecoOfferSetup from './PrecoOfferSetup';
import PrecoSetupPaymentPlanModal from './PrecoSetupPaymentPlanModal';
import { hydrateOffer, usePrecoState } from './precoState';
import OfferEligibilityModal from '../../OfferEligibilityModal';
import { useAccountLevelActions } from '../../CreditAccountPage';
import useModalControls from 'hooks/useModalControls';
import { STATUS } from 'utilities/constants';
import { hasPrecoOffer } from 'utilities/helpers';

import {
  GET_CURRENT_PRECO_OFFER,
  GET_PRECO_ELIGIBILITY,
  GET_AVAILABLE_PRECO_OFFERS,
} from 'queries/prechargeoff';
import useThirdPartyInfo from 'hooks/useThirdPartyInfo';

const CollectionsSection = () => {
  const { accountId } = useParams();
  const { enablePrecoPaymentPlans } = useFlags();
  const offerEligibilityModal = useModalControls();
  const setupPaymentPlanModal = useModalControls();
  const { madeImmediatePayment: refreshingPrecoAfterPayment } =
    useAccountLevelActions();

  const precoOfferQuery = useQuery(GET_CURRENT_PRECO_OFFER, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const precoOffer = precoOfferQuery.data?.prechargeoffSettlementOffer;
  const hasActiveOffer = hasPrecoOffer(precoOffer);

  const precoEligibilityQuery = useQuery(GET_PRECO_ELIGIBILITY, {
    variables: { accountId },
    skip: !accountId || !precoOfferQuery.data || hasActiveOffer,
    fetchPolicy: 'no-cache',
  });

  const availablePrecoOffersQuery = useQuery(GET_AVAILABLE_PRECO_OFFERS, {
    variables: { accountId },
    skip: !accountId || !precoEligibilityQuery.data?.precoEligibility?.eligible,
    fetchPolicy: 'no-cache',
  });

  const thirdParty = useThirdPartyInfo(accountId);
  const { stepIndex } = usePrecoState();
  const setupFlowActive = stepIndex > 0;

  const eligibility = precoEligibilityQuery.data?.precoEligibility;
  const availableOffers = availablePrecoOffersQuery.data?.precoOffers;

  const eligibilityChecks = eligibility?.checks || [];

  const handleSetupPaymentPlan = () => {
    hydrateOffer(precoOffer);
    setupPaymentPlanModal.show();
  };

  const renderOfferDashboard = () => {
    if (hasActiveOffer && !setupFlowActive) {
      return (
        <PrecoOfferDetails
          offer={precoOffer.data}
          thirdParty={thirdParty}
          onSetupPaymentPlan={handleSetupPaymentPlan}
          createdAt={precoOffer.createdAt}
        />
      );
    }

    if (setupFlowActive) {
      return <PrecoOfferSetup thirdParty={thirdParty} />;
    }

    if (!precoEligibilityQuery.data) {
      return null;
    }

    if (!eligibility?.eligible) {
      return (
        <SectionMessage
          status={STATUS.WARNING}
          icon={WarningOutlined}
          text="This account is not eligible for a Pre-Chargeoff settlement offer."
          buttons={[
            {
              text: 'View Eligibility Criteria',
              onClick: offerEligibilityModal.show,
            },
          ]}
        />
      );
    }

    return (
      <QueryBoundary
        query={availablePrecoOffersQuery}
        mode={QueryBoundary.MODE.MESSAGE}
        loadingMessage="Loading Pre-Chargeoff Offers"
      >
        {availableOffers && (
          <PrecoOfferSetup
            offers={availableOffers.offers}
            outstandingBalance={availableOffers.outstandingBalance}
            thirdParty={thirdParty}
          />
        )}
      </QueryBoundary>
    );
  };

  return (
    <>
      <QueryBoundary
        query={precoOfferQuery}
        mode={QueryBoundary.MODE.MESSAGE}
        loadingOverride={refreshingPrecoAfterPayment}
        loadingMessage="Loading Pre-Chargeoff Programs"
      >
        {hasActiveOffer ? (
          renderOfferDashboard()
        ) : (
          <QueryBoundary
            query={precoEligibilityQuery}
            mode={QueryBoundary.MODE.MESSAGE}
            loadingMessage="Checking Pre-Chargeoff Eligibility"
          >
            {renderOfferDashboard()}
          </QueryBoundary>
        )}
      </QueryBoundary>
      <OfferEligibilityModal
        {...offerEligibilityModal}
        eligibilityChecks={eligibilityChecks}
        eligible={eligibility?.eligible}
        offer="Pre-Chargeoff Settlement"
      />
      {enablePrecoPaymentPlans && (
        <PrecoSetupPaymentPlanModal {...setupPaymentPlanModal} />
      )}
    </>
  );
};

export default CollectionsSection;
